/* Live-Prüfung des Warenkorbs gegen den Lieferanten.

   Grundlage ist die Übergabe vom 11.08.2026 (`CART_CHECK_HANDOFF.md`).
   Verbindlich: Geprüft wird beim Öffnen des Warenkorbs, bei jeder
   Mengenänderung und noch einmal unmittelbar vor dem Checkout. Bei fehlender
   Ware, zu kleiner Menge, misslungener Abfrage oder Preisänderung bleibt der
   Checkout gesperrt, bis der Kunde den aktualisierten Warenkorb gesehen hat.
   Netto-EK, Lieferantenname und interne IDs kommen hier nicht vor und dürfen
   auch nie hereinkommen.

   Zwei Abweichungen von der Übergabe, beide aus demselben Grund: Dieser Laden
   benutzt Dawns Warenkorbschublade nicht. `settings.cart_type` steht auf
   `notification`, `snippets/cart-drawer.liquid` wird nie gerendert. Die
   Warenkorboberfläche ist der angedockte Korb am rechten Rand
   (`sections/tire-cart-dock.liquid`).

     1. Die Auslöser hängen zusätzlich am Dock (`dockBeobachten`). Ohne das
        griff keiner der drei Prüfzeitpunkte: Der Dock kennt weder
        `cart-drawer` noch Dawns `cart-items` und meldet Änderungen über
        `vantiq:korb-geaendert`, nicht über Dawns Pub/Sub.

     2. Der Checkout-Knopf wird nicht über `disabled` gesperrt, sondern über
        `aria-disabled` und einen abgefangenen Klick. Ein `disabled`-Button
        sendet keine Klick-Ereignisse — der abfangende Handler kam damit nie
        zum Zug, und der Dock-Knopf „Zur Kasse" blieb auf jeder Seite tot, ohne
        Meldung und ohne Weg zurück. Gesperrt ist er weiterhin: Der Klick löst
        eine frische Prüfung aus und wird nur durchgelassen, wenn sie besteht.
        Das ist die einzige Fassung, in der Abnahmetest 6 überhaupt bestehen
        kann — vorher führte ein API-Ausfall in eine Sackgasse, aus der nur
        Neuladen half. */
(() => {
  const config = window.vantiqCartCheck || {};
  if (!config.endpoint) return;

  const state = {
    promise: null,
    verifiedAt: 0,
    checkoutAllowed: false,
    refreshRequired: false,
    bypassSubmit: false,
    lastCartToken: '',
    lastCart: null,
    pageLeaving: false,
  };
  const checkoutSelector = '[name="checkout"], #checkout, #CartDrawer-Checkout, .korb-dock__checkout';
  const statusSelector = '[data-vantiq-cart-check-status]';

  /* Alle Texte kommen aus `snippets/vantiq-texte.liquid`, das sie aus den
     Sprachdateien rendert. Diese Datei geht als Asset an den Browser und wird
     nicht durch Liquid geschickt — deutscher Text darin wäre in jeder Sprache
     deutsch. Fehlt die Brücke, steht der Schlüssel da; das ist hässlich, aber
     eindeutig, und es kann nur passieren, wenn das Layout kaputt ist. */
  const T = (schluessel, werte) =>
    (window.vantiqText ? window.vantiqText(schluessel, werte) : schluessel);

  /* Zahl- und Währungsformat gehören zur Sprache, nicht zum Code: Ein fest
     verdrahtetes `de-CH` schrieb im französischen Laden weiter deutsche
     Zahlen. Die Werte kommen aus derselben Brücke. */
  const money = (value) => {
    const format = window.VANTIQ_FORMAT || {};
    return new Intl.NumberFormat(format.locale || 'de-CH', {
      style: 'currency',
      currency: format.currency || 'CHF',
    }).format(Number(value || 0));
  };

  /* Die Warenkorbadresse. `window.routes.cart_url` setzt Dawn aus Liquid und
     traegt damit das Sprachpraefix; der Rueckfall baut es aus
     `Shopify.routes.root` selbst zusammen. Ein festes '/cart' waere in der
     franzoesischen Ansicht ein Sprung zurueck ins Deutsche. */
  const korbAdresse = () =>
    window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`.replace('//', '/');

  function statusNodes() {
    const gueltigerHost = '.korb-dock__fuss, .cart__blocks, .cart-drawer__footer';
    const alleNodes = [...document.querySelectorAll(statusSelector)];
    /* Eine laufende Prüfung kann genau in dem Moment fertig werden, in dem die
       Section-Rendering-API den Dock ersetzt. Frühere Fassungen erzeugten dann
       einen Ersatz in #MainContent. Dieser blieb als losgelöste Erfolgsmeldung
       unter der Navigation stehen und wurde bei jeder weiteren Prüfung erneut
       befüllt. Statusmeldungen gehören ausschliesslich zur Warenkorboberfläche. */
    for (const node of alleNodes) {
      if (!node.closest(gueltigerHost)) node.remove();
    }
    let nodes = alleNodes.filter((node) => node.isConnected && node.closest(gueltigerHost));
    if (nodes.length) return nodes;
    /* Falls eine Warenkorbvorlage den Statuscontainer nicht mitbringt, darf der
       Notnagel nur innerhalb einer echten Warenkorboberfläche entstehen. */
    const host = document.querySelector(gueltigerHost);
    if (!host) return [];
    const node = document.createElement('div');
    node.className = 'vantiq-cart-check';
    node.dataset.vantiqCartCheckStatus = '';
    node.setAttribute('role', 'status');
    node.setAttribute('aria-live', 'polite');
    host.prepend(node);
    return [node];
  }

  /* Eine Aktion ist entweder ein Knopf (`handler`) oder ein Verweis (`href`) —
     die Kontaktanfrage muss ein echtes `<a href="mailto:">` sein, sonst öffnet
     sie auf dem Telefon kein Mailprogramm. */
  function aktionsknoten(aktion, haupt) {
    const klasse = `vantiq-cart-check__action`
      + (haupt ? ' vantiq-cart-check__action--haupt' : '')
      + (aktion.variante ? ` vantiq-cart-check__action--${aktion.variante}` : '');
    if (aktion.href) {
      const link = document.createElement('a');
      link.className = klasse;
      link.href = aktion.href;
      link.textContent = aktion.label;
      return link;
    }
    const button = document.createElement('button');
    button.type = 'button';
    button.className = klasse;
    button.textContent = aktion.label;
    button.addEventListener('click', aktion.handler, { once: true });
    return button;
  }

  function setStatus(type, messages, action = null, secondAction = null) {
    const list = Array.isArray(messages) ? messages.filter(Boolean) : [messages].filter(Boolean);
    for (const node of statusNodes()) {
      node.className = `vantiq-cart-check vantiq-cart-check--${type}`;
      node.hidden = list.length === 0;
      node.replaceChildren();
      if (!list.length) continue;
      /* Zeichen und Text stehen in einer eigenen Zeile, damit die farbige Kante
         nur den Befund umfasst. Lag sie am ganzen Block, lief neben dem blauen
         Aktionsknopf ein hoher farbiger Balken mit. */
      const zeile = document.createElement('div');
      zeile.className = 'vantiq-cart-check__zeile';
      const icon = document.createElement('span');
      icon.className = 'vantiq-cart-check__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = type === 'success' ? '✓' : type === 'error' ? '!' : 'i';
      const content = document.createElement('div');
      content.className = 'vantiq-cart-check__content';
      for (const message of list) {
        const line = document.createElement('p');
        line.textContent = message;
        content.append(line);
      }
      zeile.append(icon, content);
      node.append(zeile);

      if (action || secondAction) {
        const leiste = document.createElement('div');
        leiste.className = 'vantiq-cart-check__aktionen';
        if (action) leiste.append(aktionsknoten(action, true));
        if (secondAction) leiste.append(aktionsknoten(secondAction, false));
        node.append(leiste);
      }
    }
  }

  function checkoutButtons() {
    return [...document.querySelectorAll(checkoutSelector)];
  }

  /* Sperren heisst hier: sichtbar und für Hilfsmittel als gesperrt ausweisen,
     aber klickbar lassen. Der Klick landet im abfangenden Handler, der
     erneut prüft — so bleibt aus der Sperre immer ein Weg heraus. */
  function setCheckoutEnabled(enabled, checking = false) {
    for (const button of checkoutButtons()) {
      button.setAttribute('aria-disabled', enabled ? 'false' : 'true');
      button.classList.toggle('vantiq-cart-check__checkout--blocked', !enabled);
      if (checking) button.dataset.vantiqChecking = 'true';
      else delete button.dataset.vantiqChecking;
    }
    document.documentElement.classList.toggle('vantiq-cart-checkout-blocked', !enabled);
  }

  function invalidate() {
    state.verifiedAt = 0;
    state.checkoutAllowed = false;
    state.refreshRequired = false;
    state.lastCart = null;
    setCheckoutEnabled(false);
  }

  function eigenschaft(item, name) {
    const wert = item?.properties?.[name];
    return typeof wert === 'string' ? wert.trim() : '';
  }

  function partnerAusAltwert(item) {
    const sichtbar = eigenschaft(item, 'Montagepartner');
    const teile = sichtbar.split(/\s+—\s+/, 2);
    const name = teile[0]?.trim() || '';
    const adresse = teile[1]?.trim() || '';
    const komma = adresse.lastIndexOf(',');
    const strasse = komma >= 0 ? adresse.slice(0, komma).trim() : '';
    const ortsteil = komma >= 0 ? adresse.slice(komma + 1).trim() : '';
    const treffer = ortsteil.match(/^(\d{4})\s+(.+)$/);
    return {
      name,
      street: strasse,
      zip: treffer?.[1] || '',
      city: treffer?.[2]?.trim() || '',
      canton: '',
      phone: '',
    };
  }

  function partnerAusZeile(item) {
    const alt = partnerAusAltwert(item);
    return {
      name: eigenschaft(item, '_Montagepartner_Name') || alt.name,
      street: eigenschaft(item, '_Montagepartner_Strasse') || alt.street,
      zip: eigenschaft(item, '_Montagepartner_PLZ') || alt.zip,
      city: eigenschaft(item, '_Montagepartner_Ort') || alt.city,
      canton: eigenschaft(item, '_Montagepartner_Kanton') || alt.canton,
      phone: eigenschaft(item, '_Montagepartner_Telefon') || alt.phone,
    };
  }

  function lieferplan(cart) {
    const versandzeilen = (cart.items || []).filter((item) => item.requires_shipping !== false);
    const partnerzeilen = versandzeilen.filter(
      (item) => eigenschaft(item, 'Lieferung') === 'Montagepartner'
    );

    if (!partnerzeilen.length) return { art: 'heim', addresses: [] };
    if (partnerzeilen.length !== versandzeilen.length) {
      return { art: 'fehler', text: T('cart_check.partner_mixed') };
    }

    const partner = partnerzeilen.map(partnerAusZeile);
    if (partner.some((eintrag) => !eintrag.name || !eintrag.street || !eintrag.zip || !eintrag.city)) {
      return { art: 'fehler', text: T('cart_check.partner_missing') };
    }

    const signaturen = new Set(
      partner.map((eintrag) => [eintrag.name, eintrag.street, eintrag.zip, eintrag.city].join('|'))
    );
    if (signaturen.size !== 1) {
      return { art: 'fehler', text: T('cart_check.partner_multiple') };
    }

    const ziel = partner[0];
    const deliveryAddress = {
      company: ziel.name,
      address1: ziel.street,
      city: ziel.city,
      zip: ziel.zip,
      countryCode: 'CH',
    };
    if (ziel.canton) deliveryAddress.provinceCode = ziel.canton;
    if (ziel.phone) deliveryAddress.phone = ziel.phone;

    return {
      art: 'partner',
      addresses: [{
        selected: true,
        oneTimeUse: true,
        validationStrategy: 'COUNTRY_CODE_ONLY',
        address: { deliveryAddress },
      }],
    };
  }

  function storefrontToken() {
    const knoten = document.getElementById('shopify-features');
    if (!knoten) return '';
    try {
      return String(JSON.parse(knoten.textContent || '{}').accessToken || '');
    } catch (_) {
      return '';
    }
  }

  async function checkoutMitLieferadresse(cart, addresses) {
    const token = storefrontToken();
    if (!token || !cart?.token) throw new Error('storefront_cart_unavailable');
    const cartId = String(cart.token).startsWith('gid://shopify/Cart/')
      ? String(cart.token)
      : `gid://shopify/Cart/${cart.token}`;
    const query = `mutation VantiqCheckoutAddress($cartId: ID!, $addresses: [CartSelectableAddressInput!]!) {
      cartDeliveryAddressesReplace(cartId: $cartId, addresses: $addresses) {
        cart { checkoutUrl }
        userErrors { field message code }
        warnings { code message target }
      }
    }`;
    const response = await fetch(`${window.location.origin}/api/2026-07/graphql.json`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({ query, variables: { cartId, addresses } }),
    });
    if (!response.ok) throw new Error(`storefront_cart_http_${response.status}`);
    const payload = await response.json();
    const ergebnis = payload?.data?.cartDeliveryAddressesReplace;
    if (payload?.errors?.length || ergebnis?.userErrors?.length || !ergebnis?.cart?.checkoutUrl) {
      throw new Error('storefront_cart_address_rejected');
    }
    return ergebnis.cart.checkoutUrl;
  }

  async function checkoutVorbereiten() {
    const cart = state.lastCart || await cartData();
    const plan = lieferplan(cart);
    if (plan.art === 'fehler') {
      state.checkoutAllowed = false;
      setCheckoutEnabled(false);
      setStatus('error', plan.text);
      return 'gesperrt';
    }

    if (plan.art === 'partner') {
      setCheckoutEnabled(false, true);
      setStatus('checking', T('cart_check.partner_preparing'));
    }

    try {
      const checkoutUrl = await checkoutMitLieferadresse(cart, plan.addresses);
      state.pageLeaving = true;
      window.location.assign(checkoutUrl);
      return 'weitergeleitet';
    } catch (error) {
      console.error('[VANTIQ Montagepartner-Checkout] fehlgeschlagen:', error);
      if (plan.art === 'heim') return 'normal';
      state.checkoutAllowed = false;
      setCheckoutEnabled(false);
      setStatus(
        'error',
        T('cart_check.partner_checkout_error'),
        { label: T('cart_check.retry'), handler: () => window.location.reload() }
      );
      return 'gesperrt';
    }
  }

  /* Ein Fehler mit Ursache. Vorher warf jeder Zweig einen nackten `Error`, und
     `verify()` machte daraus immer denselben Satz „Prüfung nicht erreichbar" —
     egal ob der Warenkorb nicht lud, das Netz wegblieb oder der Dienst den
     Posten ablehnte. Die Übergabe nennt „nicht zuordenbare EAN" ausdrücklich
     als eigenen Sperrgrund; ohne Unterscheidung ist er nicht auffindbar, und
     eine Fehlersuche im Laden beginnt bei null. */
  class PruefFehler extends Error {
    constructor(art, ursache, nutzlast) {
      super(art);
      this.art = art;
      this.ursache = ursache;
      this.nutzlast = nutzlast;
    }
  }

  async function cartData() {
    const adresse = `${korbAdresse()}.js`;
    const wiederholungenBei499 = [300, 800];
    let response;

    for (let versuch = 0; versuch <= wiederholungenBei499.length; versuch += 1) {
      try {
        response = await fetch(adresse, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store',
        });
      } catch (fehler) {
        throw new PruefFehler('korb_netz', fehler);
      }

      if (response.ok) break;

      /* Shopify-Edges liefern vereinzelt 499, wenn ein Warenkorbabruf kurz
         vor Abschluss abgebrochen wird. Zwei kurze Wiederholungen verhindern,
         dass ein solcher Momentfehler sofort Checkout und Monitor ausloest.
         Die Freigabe bleibt waehrenddessen geschlossen; erst ein erfolgreicher
         Abruf darf die eigentliche Warenkorbpruefung starten. */
      if (response.status === 499 && versuch < wiederholungenBei499.length) {
        await new Promise((resolve) => setTimeout(resolve, wiederholungenBei499[versuch]));
        continue;
      }

      throw new PruefFehler('korb_status', new Error(`HTTP ${response.status}`));
    }

    try {
      return await response.json();
    } catch (fehler) {
      throw new PruefFehler('korb_json', fehler);
    }
  }

  function cartItems(cart) {
    const grouped = new Map();
    state.korbZeilen = new Map();
    for (const item of cart.items || []) {
      const variantId = String(item.variant_id || item.id || '').replace(/\D/g, '');
      if (!variantId) continue;
      grouped.set(variantId, (grouped.get(variantId) || 0) + Number(item.quantity || 0));
      /* Titel und SKU aus dem Warenkorb behalten: Sie werden für die
         Kontaktanfrage gebraucht, falls die Prüfantwort keine eigenen Angaben
         mitliefert. Der Kunde soll nicht „Ein Reifen" an uns schreiben. */
      if (!state.korbZeilen.has(variantId)) {
        state.korbZeilen.set(variantId, {
          titel: [item.product_title, item.variant_title].filter(Boolean).join(' ') || item.title || '',
          sku: item.sku || '',
          /* Shopify kann den Produktpreis bereits geändert haben, während
             eine bestehende Warenkorbzeile noch den alten Preis trägt. Der
             Warenkorbcheck muss deshalb nicht nur den Produktpreis vom
             Server, sondern auch den tatsächlich im Korb gespeicherten
             Grundpreis vergleichen. `original_price` ist absichtlich vor
             `final_price`: Ein gültiger Rabatt darf nicht wie ein veralteter
             Produktpreis behandelt werden. */
          preiseRappen: [],
        });
      }
      const zeile = state.korbZeilen.get(variantId);
      const zeilenpreis = Number(item.original_price ?? item.price);
      if (zeile && Number.isFinite(zeilenpreis)) zeile.preiseRappen.push(zeilenpreis);
    }
    /* Die gewünschte Menge steht hier fest, egal was die Antwort zurückmeldet.
       Sie als `requested_quantity` aus der Antwort zu lesen, war ein Fehler:
       Fehlt das Feld, wäre die Menge 0 — und mit 0 gewünschten Stück gilt jeder
       Reifen als nicht bestellbar. Der Test hat genau das gefangen. */
    for (const [id, menge] of grouped) {
      const zeile = state.korbZeilen.get(id);
      if (zeile) zeile.menge = menge;
    }
    return [...grouped].map(([variant_id, quantity]) => ({ variant_id, quantity }));
  }

  async function requestCheck(items) {
    /* Der dokumentierte Produktionsfehler war ein einzelner, vom Browser
       abgebrochener POST (HTTP 499 am Server); dieselbe Prüfung gelang eine
       Sekunde später. Deshalb werden ausschliesslich Netz-/Abbruchfehler kurz
       wiederholt. HTTP-Antworten oder fachliche Ablehnungen werden nie
       wiederholt. Ein Gesamtbudget verhindert, dass der Kunde minutenlang in
       einer scheinbar laufenden Prüfung hängen bleibt. */
    const startedAt = Date.now();
    const totalBudgetMs = Math.max(1000, Number(config.requestTotalBudgetMs || 15_000));
    const attemptBudgetMs = Math.max(500, Number(config.requestAttemptBudgetMs || 6_000));
    const retryDelays = Array.isArray(config.requestRetryDelaysMs)
      ? config.requestRetryDelaysMs.slice(0, 2).map((value) => Math.max(0, Number(value) || 0))
      : [400, 1_000];
    let lastNetworkError = null;

    for (let attempt = 0; attempt <= retryDelays.length; attempt += 1) {
      if (state.pageLeaving) throw new PruefFehler('dienst_netz', new Error('page_leaving'));
      const remainingMs = totalBudgetMs - (Date.now() - startedAt);
      if (remainingMs <= 0) break;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), Math.min(attemptBudgetMs, remainingMs));
      let response;
      try {
        response = await fetch(config.endpoint, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-store',
          signal: controller.signal,
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });
      } catch (fehler) {
        lastNetworkError = fehler;
        if (attempt >= retryDelays.length || state.pageLeaving) break;
        const delay = Math.min(retryDelays[attempt], totalBudgetMs - (Date.now() - startedAt));
        if (delay > 0) await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      } finally {
        clearTimeout(timeout);
      }
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new PruefFehler('dienst_status', new Error(`HTTP ${response.status}`), payload);
      /* Technischer Text, geht in die Konsole und nie an den Kunden — bleibt
         deshalb englisch wie der Rest der Fehlerkennungen. */
      if (!payload?.ok) throw new PruefFehler('dienst_abgelehnt', new Error(payload?.error || 'no reason given'), payload);
      return payload;
    }

    /* Hier landet auch der abgewiesene CORS-Vorflug: Der Browser meldet ihn
       als gewöhnlichen Netzfehler, ohne Status. Die Unterscheidung steht dann
       nur in der Konsole des Browsers. */
    throw new PruefFehler('dienst_netz', lastNetworkError || new Error('request_time_budget_exhausted'));
  }

  /* Wenn /cart.js scheitert, existieren noch keine gelesenen Warenkorbitems.
     Auf der Warenkorbseite und im Desktop-Dock sind die von Shopify bereits
     gerenderten Positionen trotzdem sichtbar. Nur diese konkreten Zeilen
     dienen als gekennzeichneter Diagnose-Rueckfall; fehlt auch dort eine
     eindeutige Varianten-ID/Menge, bleibt das Produkt unbekannt. */
  function monitorDomItems() {
    const cartRows = [...document.querySelectorAll('#main-cart-items tr.cart-item')];
    const drawerRows = [...document.querySelectorAll('#CartDrawer-CartItems tr.cart-item')];
    const rows = cartRows.length ? cartRows : drawerRows.length ? drawerRows
      : [...document.querySelectorAll('tire-korb-dock .korb-dock__liste > .korb-zeile')];
    return rows.slice(0, 20).map((row) => {
      const input = row.querySelector('input[data-quantity-variant-id]');
      const variantId = String(input?.dataset.quantityVariantId || row.dataset.variantId || '');
      const quantity = Number(input?.value || row.dataset.quantity);
      if (!/^\d{1,24}$/.test(variantId) || !Number.isInteger(quantity) || quantity < 1 || quantity > 9999) return null;
      const title = String(row.querySelector('.cart-item__name, .korb-zeile__name')?.textContent || '')
        .replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 160);
      return { variant_id: variantId, quantity, product_title: title };
    }).filter(Boolean);
  }

  /* Technische Fehler werden zusätzlich an den privaten Monitor gemeldet.
     Nur oeffentliche Produktnamen, Varianten-IDs und Mengen duerfen dazu;
     keine Warenkorbkennung, Kundendaten, URL-Parameter oder Preise. Der
     Monitor darf den Kundenablauf nie blockieren. */
  function reportFailure(error, items) {
    const art = error instanceof PruefFehler ? error.art : 'unerwartet';
    const statusMatch = String(error?.ursache?.message || '').match(/HTTP\s+(\d{3})/i);
    const httpStatus = statusMatch ? Number(statusMatch[1]) : 0;
    const publicError = String(error?.nutzlast?.error || '').toLowerCase();
    const endpoint = `${String(config.endpoint).replace(/\/+$/, '')}/monitor`;
    const checkedItems = (Array.isArray(items) ? items : []).slice(0, 20).map((item) => {
      const variantId = String(item.variant_id || '');
      const quantity = Number(item.quantity);
      if (!/^\d{1,24}$/.test(variantId) || !Number.isInteger(quantity) || quantity < 1 || quantity > 9999) return null;
      const title = String(state.korbZeilen?.get(variantId)?.titel || '')
        .replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 160);
      return { variant_id: variantId, quantity, product_title: title };
    }).filter(Boolean);
    const safeItems = checkedItems.length ? checkedItems : monitorDomItems();
    const productSource = checkedItems.length ? 'cart_json' : safeItems.length ? 'cart_html' : 'unknown';

    fetch(endpoint, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      cache: 'no-store',
      keepalive: true,
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({
        schema_version: 1,
        error_code: art,
        phase: art.startsWith('dienst_') ? 'service_request' : 'cart_read',
        http_status: httpStatus,
        public_error: /^[a-z0-9_.:-]{1,80}$/.test(publicError) ? publicError : '',
        page: window.location.pathname || '/cart',
        product_source: productSource,
        items: safeItems,
      }),
    }).catch(() => {});
  }

  /* --- Befunde -----------------------------------------------------------

     Ein Reifen kann mehrere Befunde gleichzeitig tragen — teurer geworden UND
     nicht mehr bestellbar ist keine Ausnahme, sondern der Normalfall, wenn ein
     Lieferant ein Modell ausrangiert. Deshalb sammelt jede Zeile ihre Befunde
     als Liste, und erst danach entscheidet der **strengste** über Ton, Sperre
     und Hauptknopf. Vorher gewann `cart_refresh_required`: Ein nicht
     bestellbarer Reifen bekam dann bernsteinfarbenes „Warenkorb
     aktualisieren" — ein Knopf, der die Sperre gar nicht auflösen kann. */

  const RANG = { info: 0, hinweis: 1, preis: 2, menge: 3, nicht_bestellbar: 4 };

  const strengsterRang = (befunde) => befunde.reduce((m, b) => Math.max(m, b.rang), RANG.info);

  const zahl = (wert) => (wert === null || wert === undefined || wert === '' ? null : Number(wert));

  /* Die drei Lagerstufen:

       1  ab Lager         2–4 Tage
       2  ab Aussenlager   5–10 Tage
       3  nicht bestellbar

     **Die Stufe hängt am Artikel, nicht an der Stückzahl.** Das Backend liest
     beim Lieferanten eine Ampel plus „sofort"/„ca. 3 Tage" je EAN und leitet
     daraus genau eine Stufe für den ganzen Posten ab. Vier Stück desselben
     Reifens teilweise ab Lager und teilweise ab Aussenlager zu beziehen, gibt
     es in diesen Daten nicht — eine Aufteilung je Stück wäre erfunden.

     Der gemischte Fall existiert trotzdem, nur eine Ebene höher: **zwei
     verschiedene Reifen im selben Warenkorb aus verschiedenen Stufen.** Der
     wird in `renderResult` aus den Postenwerten gebildet und ist heute schon
     vollständig ableitbar — `availability` und `delivery_time` stehen je
     Posten in der Antwort.

     Die Zahl an einer gelben Ampel ist nur der Sofortbestand, keine
     Höchstmenge. Ein Mengenproblem darf deshalb nur entstehen, wenn das
     Backend künftig ausdrücklich eine echte Bestellgrenze mit
     `requested_quantity_available === false` und `max_quantity` meldet. */
  function lagerlage(item, gewuenscht) {
    const max = zahl(item.max_quantity);
    const unterdeckt = item.requested_quantity_available === false;
    return {
      deckung: unterdeckt && max !== null ? max : gewuenscht,
      unterdeckt,
    };
  }

  /* Die massgebliche Lieferangabe eines Postens. `new_*` gewinnt, weil es den
     soeben geprüften Stand trägt; `availability`/`delivery_time` sind der
     Rückfall für die Einzelabfrage. */
  const lieferstufe = (item) => item.new_availability || item.availability || '';
  const lieferfrist = (item) => item.new_delivery_time || item.delivery_time || '';

  function befundeFuer(item, gewuenscht) {
    const befunde = [];
    const korbZeile = state.korbZeilen?.get(String(item.variant_id));
    const titel = item.title || korbZeile?.titel
      || T('cart_check.generic_tire');

    if (!item.ok) {
      befunde.push({ rang: RANG.nicht_bestellbar, titel, kopf: T('cart_check.check_failed_head'),
        text: T('cart_check.check_failed_text') });
      return { titel, befunde, lage: null };
    }

    const lage = lagerlage(item, gewuenscht);
    const nichtBestellbar = item.orderable === false || (lage.bekannt && lage.deckung === 0);

    /* Preis nur melden, wenn der Reifen überhaupt noch bestellbar ist. Der
       Lieferant liefert EK und Verfügbarkeit unabhängig voneinander, die
       Kombination „teurer geworden UND nicht lieferbar" kann also durchaus
       zurückkommen — für den Kunden ist sie aber sinnlos: Auf einer Zeile, die
       er entfernen muss, ist der neue Preis keine Information, sondern Lärm.
       Bei einer bloss zu knappen Menge bleibt die Meldung dagegen stehen — dort
       kauft er ja weiter, nur weniger. */
    const sollpreisRappen = item.new_price == null ? null : rappen(item.new_price);
    const veralteteKorbpreise = sollpreisRappen === null ? []
      : (korbZeile?.preiseRappen || []).filter((preis) => preis !== sollpreisRappen);
    const korbpreisVeraltet = veralteteKorbpreise.length > 0;
    const preisGeaendert = Boolean(item.price_changed) || korbpreisVeraltet;
    const alterPreis = item.price_changed
      ? item.old_price
      : (korbpreisVeraltet ? veralteteKorbpreise[0] / 100 : item.old_price);

    if (preisGeaendert && !nichtBestellbar) {
      befunde.push({ rang: RANG.preis, titel, kopf: T('cart_check.price_changed_head'),
        text: T('cart_check.price_changed_text', { alt: money(alterPreis), neu: money(item.new_price) }),
        preis: true });
    }

    if (nichtBestellbar) {
      befunde.push({ rang: RANG.nicht_bestellbar, titel, kopf: T('cart_check.not_orderable_head'),
        text: T('cart_check.not_orderable_text') });
    } else if (lage.unterdeckt) {
      befunde.push({ rang: RANG.menge, titel, kopf: T('cart_check.quantity_head'),
        text: T('cart_check.quantity_text', { deckung: lage.deckung, gewuenscht }),
        hoechstmenge: lage.deckung });
    } else if (item.availability_changed) {
      /* Die Lagerstufe selbst (`new_availability`) kommt heute als deutscher
         Text vom Lieferanten und bleibt deshalb deutsch, auch im
         französischen Laden. Das ist Phase B — Codes statt Klartext. Der
         Rahmen um den Wert ist ab hier übersetzt, der Wert noch nicht. */
      const zeit = lieferfrist(item);
      befunde.push({ rang: RANG.hinweis, titel, kopf: T('cart_check.delivery_changed_head'),
        text: T('cart_check.delivery_changed_text', {
          stufe: item.new_availability || T('cart_check.delivery_changed_fallback'),
          frist: zeit ? T('cart_check.delivery_changed_suffix', { zeit }) : '',
        }) });
    }

    return { titel, befunde, lage };
  }

  /* Vorgefertigte Anfrage für nicht bestellbare Reifen. Enthält Modell, Grösse,
     EAN und Stückzahl — genug, damit die Beschaffung ohne Rückfrage starten
     kann. Enthält bewusst **nicht**: Einkaufspreis, Lieferantenname, interne
     ID oder das Warenkorb-Merkmal. Eine Mail geht unverschlüsselt raus; darin
     hat nur das zu stehen, was der Kunde ohnehin sieht. */
  function anfrageAdresse(zeilen) {
    const empfaenger = config.kontakt || '';
    if (!empfaenger || !zeilen.length) return '';

    const posten = zeilen.slice(0, 10).map((z, i) => {
      const teile = [`${i + 1}) ${z.titel}`];
      if (z.ean) teile.push(`   EAN ${z.ean}`);
      teile.push(T('cart_check.enquiry_line_quantity', { menge: z.menge }));
      return teile.join('\n');
    });

    const betreff = zeilen.length === 1
      ? T('cart_check.enquiry_subject_one', { titel: zeilen[0].titel })
      : T('cart_check.enquiry_subject_many', { anzahl: zeilen.length });

    const text = [
      T('cart_check.enquiry_greeting'),
      '',
      zeilen.length === 1
        ? T('cart_check.enquiry_intro_one')
        : T('cart_check.enquiry_intro_many'),
      T('cart_check.enquiry_request'),
      '',
      ...posten,
      '',
      T('cart_check.enquiry_ask'),
      '',
      T('cart_check.enquiry_closing'),
      '',
    ].join('\n');

    return `mailto:${encodeURIComponent(empfaenger)}?subject=${encodeURIComponent(betreff)}&body=${encodeURIComponent(text)}`;
  }

  /* Menge setzen — 0 heisst entfernen.

     Über `/cart/update.js` mit `updates`, nicht über `/cart/change.js` mit
     `line`: `updates` ist nach Varianten-ID geschlüsselt und setzt damit die
     Gesamtmenge dieser Variante, auch wenn sie über mehrere Zeilen verteilt im
     Korb liegt. Genau so wird sie hier auch geprüft, nämlich zusammengefasst —
     eine Zeilennummer würde bei zwei Zeilen desselben Reifens danebengreifen.

     Auf der vollständigen Warenkorbseite wird danach neu geladen, damit
     Mengenfeld, Zeilensumme und Gesamtsumme sicher denselben Stand zeigen.
     Im angedockten Warenkorb löst `vantiq:korb-geaendert` dessen erneutes
     Rendern aus. */
  async function korbSetzen(mengen) {
    const antwort = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
      body: JSON.stringify({ updates: mengen }),
    });
    if (!antwort.ok) throw new Error('cart_update_failed');
    return antwort.json();
  }

  function istWarenkorbseite() {
    return document.body.classList.contains('template-cart') || Boolean(document.querySelector('#main-cart-items'));
  }

  const rappen = (chf) => Math.round(Number(chf) * 100);

  /* Preise im Warenkorb auffrischen.

     Der Kern des Problems: **Ein Neuladen der Seite preist nichts neu aus.**
     Shopify hält den Zeilenpreis fest, bis die Zeile selbst geändert wird —
     das Listing zeigte danach 178.90, der Warenkorb weiter 175.90, und der
     Knopf „Warenkorb aktualisieren" tat sichtbar nichts. Genau der Zustand,
     den die Sperre verhindern soll: Der Kunde sieht einen Preis, zu dem er
     nicht kaufen kann.

     Zwei Stufen, weil die erste nicht überall greift:

       1. Jede betroffene Zeile über `/cart/change.js` mit ihrer eigenen Menge
          neu setzen. Das ist die schonende Variante — Position und
          Zeileneigenschaften bleiben erhalten.
       2. Nachsehen, ob der Preis jetzt stimmt. Tut er es nicht, wird die Zeile
          entfernt und mit derselben Menge neu angelegt. Dann preist Shopify
          zwingend neu aus; die Zeile rutscht dafür ans Ende.

     Die Prüfung dazwischen ist der Punkt: Die Aktion stellt selbst fest, ob
     sie gewirkt hat, statt es zu hoffen. */
  async function preisAuffrischen(neuePreise) {
    const wurzel = window.Shopify?.routes?.root || '/';
    const senden = (pfad, koerper) => fetch(wurzel + pfad, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
      body: JSON.stringify(koerper),
    });

    let korb = await cartData();
    for (const zeile of korb.items || []) {
      if (neuePreise[String(zeile.variant_id)] === undefined) continue;
      await senden('cart/change.js', { id: zeile.key, quantity: zeile.quantity });
    }

    korb = await cartData();
    const hartnaeckig = (korb.items || []).filter((zeile) => {
      const soll = neuePreise[String(zeile.variant_id)];
      return soll !== undefined && Number(zeile.price) !== rappen(soll);
    });

    for (const zeile of hartnaeckig) {
      const menge = zeile.quantity;
      await senden('cart/change.js', { id: zeile.key, quantity: 0 });
      await senden('cart/add.js', { items: [{ id: zeile.variant_id, quantity: menge }] });
    }

    document.dispatchEvent(new CustomEvent('vantiq:korb-geaendert', { detail: { quelle: 'pruefung' } }));
  }

  function korbAktion(mengen, laufText) {
    return async () => {
      setStatus('checking', laufText);
      setCheckoutEnabled(false, true);
      try {
        await korbSetzen(mengen);
        if (istWarenkorbseite()) {
          window.location.reload();
          return;
        }
        document.dispatchEvent(new CustomEvent('vantiq:korb-geaendert', { detail: { quelle: 'pruefung' } }));
        await verify({ force: true });
      } catch (fehler) {
        setStatus('error', T('cart_check.update_failed'),
          { label: T('cart_check.to_cart'), handler: () => window.location.assign(korbAdresse()) });
      }
    };
  }

  function renderResult(payload) {
    const alle = [];
    const nichtBestellbar = [];
    const entfernen = {};
    const kuerzen = {};
    const neuePreise = {};
    let rueckfall = false;

    for (const item of payload.items || []) {
      if (item.skipped) continue;
      const zeile = state.korbZeilen?.get(String(item.variant_id));
      const gewuenscht = zeile?.menge ?? zahl(item.requested_quantity) ?? zahl(item.quantity) ?? 0;
      const { titel, befunde } = befundeFuer(item, gewuenscht);
      alle.push(...befunde);
      if (befunde.some((b) => b.rang === RANG.nicht_bestellbar) && item.ok !== false) {
        nichtBestellbar.push({
          titel,
          ean: item.ean || zeile?.sku || '',
          menge: gewuenscht,
        });
        entfernen[String(item.variant_id)] = 0;
      }
      const mengenbefund = befunde.find((b) => b.rang === RANG.menge);
      if (mengenbefund) kuerzen[String(item.variant_id)] = mengenbefund.hoechstmenge;
      if (befunde.some((b) => b.preis) && item.new_price != null) {
        neuePreise[String(item.variant_id)] = item.new_price;
      }
      if (item.review_required) rueckfall = true;
    }

    if (rueckfall) {
      alle.push({ rang: RANG.hinweis, titel: '', kopf: T('cart_check.fallback_head'),
        text: T('cart_check.fallback_text') });
    }

    /* Der gemischte Warenkorb: verschiedene Reifen aus verschiedenen Lagerstufen.

       Das ist der Fall, den es wirklich gibt — nicht die Aufteilung eines
       Postens. Er darf **nicht** als Mangel erscheinen: Beide Stufen sind
       bestellbar, „ab Aussenlager ist immer noch ab Lager sozusagen". Deshalb
       Rang `info`, also grün, und der Unterschied steht im Text.

       Bewusst ohne Versandversprechen: Ob zusammen oder getrennt geliefert
       wird, steht in diesen Daten nicht. Genannt wird nur, was zutrifft —
       welche Fristen im Korb liegen. */
    const fristen = [...new Set((payload.items || [])
      .filter((item) => !item.skipped && item.ok !== false && item.orderable !== false)
      .map((item) => lieferfrist(item))
      .filter(Boolean))];

    if (fristen.length > 1 && strengsterRang(alle) < RANG.menge) {
      alle.push({ rang: RANG.info, titel: '', kopf: T('cart_check.mixed_head'),
        text: T('cart_check.mixed_text', { fristen: fristen.join(T('cart_check.mixed_join')) }) });
    }

    const strengster = strengsterRang(alle);
    /* `cart_refresh_required` meldet nur eine Preisänderung innerhalb genau
       dieser Serveranfrage. Wurde der Produktpreis schon bei der Prüfung im
       Dock angehoben, ist er beim Öffnen von /cart unverändert — die alte
       Warenkorbzeile kann trotzdem noch falsch bepreist sein. `neuePreise`
       enthält auch diesen lokal erkannten Fall und hält den Checkout gesperrt. */
    state.refreshRequired = Boolean(payload.cart_refresh_required)
      || Object.keys(neuePreise).length > 0;
    state.checkoutAllowed = Boolean(payload.checkout_allowed)
      && !state.refreshRequired
      && strengster < RANG.preis;
    state.verifiedAt = Date.now();
    setCheckoutEnabled(state.checkoutAllowed);

    /* Die Hauptaktion muss die Sperre auch auflösen können. Deshalb hängt sie
       am strengsten Befund, nicht an `cart_refresh_required`. */
    let aktion = null;
    let zweitaktion = null;
    if (strengster === RANG.nicht_bestellbar) {
      const anzahl = Object.keys(entfernen).length;
      aktion = anzahl
        ? { label: anzahl === 1 ? T('cart_check.remove_one') : T('cart_check.remove_many', { anzahl }),
            handler: korbAktion(entfernen, T('cart_check.removing')) }
        : { label: T('cart_check.to_cart'), handler: () => window.location.assign(korbAdresse()) };
      /* Entfernen ist nicht die einzige Antwort: Was im Standardweg nicht
         verfügbar ist, lässt sich oft einzeln beschaffen. Ohne diesen zweiten
         Weg verliert der Laden die Bestellung genau dort, wo er sie noch retten
         könnte. (Kein Lieferantenname in dieser Datei — sie geht als Asset an
         den Browser.) */
      const href = anfrageAdresse(nichtBestellbar);
      if (href) zweitaktion = { label: T('cart_check.enquire'), href, variante: 'anfrage' };
    } else if (strengster === RANG.menge) {
      const eintraege = Object.entries(kuerzen).filter(([, m]) => Number.isFinite(m));
      const einzeln = eintraege.length === 1 ? eintraege[0][1] : null;
      aktion = eintraege.length
        ? { label: einzeln === null ? T('cart_check.adjust_all') : T('cart_check.adjust_to', { menge: einzeln }),
            handler: korbAktion(Object.fromEntries(eintraege), T('cart_check.adjusting')) }
        : { label: T('cart_check.to_cart'), handler: () => window.location.assign(korbAdresse()) };
    } else if (strengster === RANG.preis || state.refreshRequired) {
      aktion = {
        label: T('cart_check.refresh'),
        handler: async () => {
          setStatus('checking', T('cart_check.refreshing'));
          setCheckoutEnabled(false, true);
          try {
            if (Object.keys(neuePreise).length) await preisAuffrischen(neuePreise);
            if (istWarenkorbseite()) {
              /* Die Shopify-Warenkorbzeile ist jetzt neu bepreist, aber die
                 bereits gerenderte Liquid-Seite zeigt weiterhin den alten
                 Betrag. Erst nach dem Reload dürfen Summen und Erfolgsstatus
                 gemeinsam freigegeben werden. */
              window.location.reload();
              return;
            }
            await verify({ force: true });
          } catch (fehler) {
            console.error('[VANTIQ Warenkorbprüfung] Aktualisieren fehlgeschlagen:', fehler);
            /* Neuladen als letzter Ausweg: Es preist zwar nichts neu aus, holt
               aber wenigstens einen konsistenten Stand auf den Schirm. */
            window.location.reload();
          }
        },
      };
    }

    const ton = strengster >= RANG.menge ? 'error' : strengster >= RANG.hinweis ? 'warning' : 'success';
    /* Kurz genug für eine Zeile: In der 34-rem-Spalte stehen rund 37 Zeichen
       je Zeile zur Verfügung. „…wurden soeben geprüft." brach auf zwei Zeilen
       und liess den ruhigsten aller Zustände am unruhigsten aussehen. Dass es
       eben erst geprüft wurde, sagt schon die Tatsache, dass die Meldung
       gerade erscheint. */
    const zeilen = alle.length
      ? alle.map((b) => [b.titel, b.kopf, b.text].filter(Boolean).join(' · '))
      : [T('cart_check.ok')];
    /* Ein reiner Info-Befund (gemischter Satz) bleibt grün und bekommt die
       Bestätigungszeile davor — sonst läse sich die Auskunft wie ein Mangel. */
    if (ton === 'success' && alle.length) zeilen.unshift(T('cart_check.ok'));

    setStatus(ton, zeilen, aktion, zweitaktion);
    return state.checkoutAllowed;
  }

  async function verify({ force = false } = {}) {
    if (state.promise) return state.promise;
    if (!force && state.checkoutAllowed && Date.now() - state.verifiedAt < 90_000) return true;

    state.promise = (async () => {
      invalidate();
      setCheckoutEnabled(false, true);
      setStatus('checking', T('cart_check.checking'));
      let items = [];
      try {
        const cart = await cartData();
        items = cartItems(cart);
        state.lastCartToken = String(cart.token || '');
        state.lastCart = cart;
        if (!items.length) {
          setStatus('success', []);
          return false;
        }
        return renderResult(await requestCheck(items));
      } catch (error) {
        state.checkoutAllowed = false;
        state.verifiedAt = 0;
        setCheckoutEnabled(false);

        /* Eine Navigation schliesst die Seite und bricht offene Requests ab.
           Das ist kein Kundenfehler und darf weder Alarmmail noch kurz vor dem
           Verlassen eine neue Fehlermeldung erzeugen. */
        if (state.pageLeaving) return false;

        const art = error instanceof PruefFehler ? error.art : 'unerwartet';
        reportFailure(error, items);
        /* In die Konsole gehört die ganze Wahrheit, in die Meldung nur der
           Teil, der den Kunden angeht. Ohne diese Zeile ist im Laden nicht zu
           unterscheiden, ob das Netz weg war, der Dienst ablehnte oder der
           Auswertecode selbst gestolpert ist — alles drei sah gleich aus. */
        console.error('[VANTIQ Warenkorbprüfung] fehlgeschlagen:', art, error?.ursache || error, error?.nutzlast ?? '');

        /* Funktionen statt Schlüsselnamen: So steht `T('…')` wörtlich da, und
           `sprachen_pruefen.py` sieht beim Lesen der Datei, welche Schlüssel
           gebraucht werden. Über eine Nachschlagetabelle aus Zeichenketten
           wäre der Bezug für jede statische Prüfung unsichtbar. */
        const texte = {
          korb_netz: () => T('cart_check.error_cart'),
          korb_status: () => T('cart_check.error_cart'),
          korb_json: () => T('cart_check.error_cart'),
          dienst_netz: () => T('cart_check.error_service_net'),
          dienst_status: () => T('cart_check.error_service_status'),
          dienst_abgelehnt: () => T('cart_check.error_service_rejected'),
          unerwartet: () => T('cart_check.error_unexpected'),
        };

        /* Mit Knopf, nicht nur mit Bitte: Ohne eigene Aktion bliebe dem Kunden
           allein das Neuladen der Seite — Abnahmetest 6 verlangt eine
           verständliche Meldung, keine Sackgasse. */
        setStatus(
          'error',
          [
            (texte[art] || texte.unerwartet)(),
            T('cart_check.locked'),
            /* Kurzkennung für die Fehlersuche. Sie verrät nichts über
               Lieferant oder Einkauf, spart aber im Zweifel eine Sitzung.
               Die Kennung selbst (`dienst_netz`) bleibt unübersetzt — sie ist
               ein Schlüssel für die Fehlersuche, kein Satz. */
            T('cart_check.code', { art }),
          ],
          { label: T('cart_check.retry'), handler: () => verify({ force: true }) }
        );
        return false;
      } finally {
        state.promise = null;
      }
    })();
    return state.promise;
  }

  async function handleCheckout(event) {
    const target = event.target.closest?.(checkoutSelector);
    if (!target || state.bypassSubmit) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const allowed = await verify({ force: true });
    if (!allowed) return;
    const vorbereitet = await checkoutVorbereiten();
    if (vorbereitet !== 'normal') return;
    state.bypassSubmit = true;
    const form = target.form || target.closest('form');
    if (form) {
      if (typeof form.requestSubmit === 'function') form.requestSubmit(target);
      else form.submit();
    } else if (target.href) {
      window.location.assign(target.href);
    }
    setTimeout(() => { state.bypassSubmit = false; }, 1000);
  }

  async function handleCheckoutSubmit(event) {
    if (state.bypassSubmit) return;
    const form = event.target;
    const submitter = event.submitter;
    const isCartCheckout = submitter?.matches?.(checkoutSelector)
      || form?.matches?.('#cart, #CartDrawer-Form')
      || form?.querySelector?.('[name="checkout"]');
    if (!isCartCheckout) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const allowed = await verify({ force: true });
    if (!allowed) return;
    const vorbereitet = await checkoutVorbereiten();
    if (vorbereitet !== 'normal') return;
    state.bypassSubmit = true;
    if (typeof form.requestSubmit === 'function' && submitter) form.requestSubmit(submitter);
    else form.submit();
    setTimeout(() => { state.bypassSubmit = false; }, 1000);
  }

  document.addEventListener('click', handleCheckout, true);
  document.addEventListener('submit', handleCheckoutSubmit, true);
  window.addEventListener('pagehide', () => { state.pageLeaving = true; });
  document.addEventListener('change', (event) => {
    if (event.target.closest?.('cart-items, cart-drawer-items')) {
      invalidate();
      setTimeout(() => verify({ force: true }), 700);
    }
  }, true);

  const drawer = document.querySelector('cart-drawer');
  if (drawer) {
    new MutationObserver(() => {
      if (drawer.classList.contains('active')) verify({ force: false });
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  /* Der angedockte Warenkorb ist die eigentliche Warenkorboberfläche dieses
     Ladens — hier hängen „Öffnen" und „Mengenänderung". */
  function dockBeobachten() {
    const dock = document.querySelector('tire-korb-dock');
    if (!dock) return;

    /* Der Dock steht auf jeder Seite und bleibt offen, wenn der Kunde ihn offen
       gelassen hat (`localStorage`). „Beim Seitenaufruf offen" ist deshalb kein
       Öffnen — sonst liefe bei jedem Seitenwechsel eine Lieferantenabfrage.
       Gezählt wird nur der Wechsel von zu nach offen. */
    let warOffen = dock.classList.contains('korb-dock--offen');

    new MutationObserver((eintraege) => {
      for (const eintrag of eintraege) {
        if (eintrag.type === 'childList') {
          /* Der Dock holt sich sein Inneres über die Section Rendering API neu,
             sobald sich der Warenkorb ändert — von der Karte, vom Kaufformular
             oder aus dem Dock selbst. Danach kommen Statuscontainer und
             Knopfzustand frisch aus Liquid: Der zuletzt angezeigte Stand ist
             weg und muss neu erhoben werden. Das ersetzt einen eigenen
             Zuhörer auf `vantiq:korb-geaendert` — der löst dasselbe Nachladen
             aus und würde die Abfrage doppelt starten. */
          invalidate();
          if (dock.classList.contains('korb-dock--offen')) verify({ force: true });
          continue;
        }
        const offen = dock.classList.contains('korb-dock--offen');
        if (offen && !warOffen) verify({ force: false });
        warOffen = offen;
      }
    }).observe(dock, { attributes: true, attributeFilter: ['class'], childList: true });
  }

  document.addEventListener('DOMContentLoaded', () => {
    /* Vorsperren nur dort, wo unmittelbar danach geprüft wird. Auf allen
       anderen Seiten stünde sonst ein grauer, unerklärter „Zur Kasse"-Knopf im
       Dock; die Sicherheit hängt am abgefangenen Klick, nicht an der Optik. */
    if (document.body.classList.contains('template-cart') || document.querySelector('#main-cart-items')) {
      setCheckoutEnabled(false);
      verify({ force: true });
    }
    dockBeobachten();
  });

  if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      invalidate();
      if (document.querySelector('cart-drawer.active, #main-cart-items')) setTimeout(() => verify({ force: true }), 500);
    });
  }

  window.VantiqCartCheck = { verify, invalidate };
})();
